import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Download, Loader2, X } from 'lucide-react';
import { getTenantLogoDataUrl } from '../../services/tenantLogoApi';
import {
  formatScopeApiError,
  interiorScopeApi,
} from './interiorScopeApi';
import { buildTemplatePreviewSections } from './templatePreviewModel';
import ProjectScopeDocument from './ProjectScopeDocument';
import {
  buildScopePdfFileName,
  exportProjectScopeNodeToPdf,
} from './projectScopePdf';

const toPreviewProject = (scope) => {
  const program = scope?.program || {};
  return {
    id: scope.scopeId,
    name: program.project_address || program.client_names || 'Scope Sheet',
    clientName: program.client_names || '',
    location: program.project_address || '',
    status: scope.status || 'draft',
    scopeDate: (scope.updatedAt || '').slice(0, 10),
  };
};

const ProjectScopePreview = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const siteId =
    location.state?.siteId ||
    new URLSearchParams(location.search).get('siteId') ||
    '';
  const contentRef = useRef(null);

  const [project, setProject] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [documentKey, setDocumentKey] = useState(0);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!siteId) {
          throw new Error('Missing siteId for scope preview');
        }
        const [tpl, scope] = await Promise.all([
          interiorScopeApi.getLatestTemplate(),
          interiorScopeApi.getScope(siteId, projectId),
        ]);
        if (!active) return;
        setProject(toPreviewProject(scope));
        setSections(buildTemplatePreviewSections(tpl, scope));
      } catch (err) {
        if (!active) return;
        setError(formatScopeApiError(err));
        setProject(null);
        setSections([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [projectId, siteId]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleClose = () => {
    navigate(
      siteId
        ? `/projects?siteId=${encodeURIComponent(siteId)}`
        : '/projects',
      { state: siteId ? { siteId } : undefined }
    );
  };

  const handleDownloadPdf = async () => {
    if (isGenerating || !project) return;
    try {
      setIsGenerating(true);

      const logoUrl = await getTenantLogoDataUrl().catch(() => null);

      const container = document.createElement('div');
      container.style.cssText =
        'position:fixed;left:-10000px;top:0;z-index:-1;pointer-events:none;';
      document.body.appendChild(container);
      const root = createRoot(container);
      const exportRef = React.createRef();

      try {
        await new Promise((resolve) => {
          root.render(
            React.createElement(ProjectScopeDocument, {
              ref: exportRef,
              project,
              sections,
              logoUrl,
            })
          );
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        if (!exportRef.current) {
          throw new Error('Unable to render scope sheet for PDF.');
        }

        await exportProjectScopeNodeToPdf(
          exportRef.current,
          buildScopePdfFileName(project)
        );
      } finally {
        root.unmount();
        container.remove();
      }
    } catch (err) {
      console.error('Failed to generate scope sheet PDF', err);
      alert('Unable to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
      setDocumentKey((k) => k + 1);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex h-screen w-screen flex-col bg-gray-800"
      style={{ colorScheme: 'dark' }}
    >
      <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-700 bg-gray-900 px-6 py-4">
        <div>
          <h2 className="text-base font-semibold text-white">
            Scope Sheet Preview
          </h2>
          <p className="text-xs text-gray-400">
            {project?.clientName || 'Untitled client'} &middot;{' '}
            {project?.name || 'Untitled project'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={isGenerating || loading || !project}
            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isGenerating ? 'Generating...' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 transition hover:bg-gray-700"
          >
            <X className="h-4 w-4" /> Close
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-gray-600 p-6">
        {loading ? (
          <div className="flex h-full items-center justify-center gap-2 text-gray-200">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading preview...</span>
          </div>
        ) : error && !project ? (
          <div className="mx-auto max-w-lg rounded-lg border border-red-400 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : (
          <div className="flex items-start justify-center">
            <ProjectScopeDocument
              key={documentKey}
              ref={contentRef}
              project={project}
              sections={sections}
            />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ProjectScopePreview;
